"use client";

import { ArrowRight, Building2, Check, MapPin, Truck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ProtectionPortfolio() {
  const header = useRevealOnScroll<HTMLDivElement>();
  const services = [{
    icon: Truck,
    title: "Auto Insurance",
    description: "Comprehensive protection for your vehicle, ensuring you stay on the road with confidence and financial security.",
    features: ["Collision and liability coverage", "24/7 roadside assistance", "Competitive premium rates"],
    popular: false,
    to: "/ClaimsCenterPage"
  }, {
    icon: MapPin,
    title: "Home Insurance",
    description: "Safeguard your most valuable asset with tailored policies that cover structural damage, theft, and natural disasters.",
    features: ["Full property replacement value", "Personal liability protection", "Natural disaster coverage"],
    popular: true,
    to: "/ClaimsCenterPage"
  }, {
    icon: Building2,
    title: "Business Insurance",
    description: "Robust coverage designed to protect your enterprise from operational risks, liability claims, and property loss.",
    features: ["General liability insurance", "Professional indemnity coverage", "Customized risk management"],
    popular: false,
    to: "/ClaimsCenterPage"
  }];
  return <section data-section-id="681" id="protection-portfolio" className="relative bg-background text-foreground py-24 lg:py-32 overflow-hidden">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 hidden lg:block opacity-40">
        <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg" fill="none" preserveAspectRatio="none" viewBox="0 0 1200 600">
          <path d="M 100 400 Q 400 200 600 400 T 1100 400" stroke="var(--primary)" strokeWidth="2" strokeDasharray="6 10" opacity="0.35" />
          <path d="M 50 500 Q 350 350 700 500 T 1150 450" stroke="var(--primary)" strokeWidth="2" strokeDasharray="4 12" opacity="0.25" />
        </svg>
      </div>

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div ref={header.ref} className={`max-w-3xl mx-auto text-center mb-16 lg:mb-20 transition-all duration-700 ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="text-sm font-bold uppercase tracking-[0.15em] text-primary mb-5">
            Our Coverage
          </p>
          <h2 className="font-default text-4xl sm:text-5xl lg:text-[3.5rem] font-normal leading-[1.05] text-foreground mb-6">
            Insurance solutions for your{" "}
            <span className="italic text-primary">peace of mind</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-[600px] mx-auto leading-relaxed">
            Vanguard Shield Insurance provides professional, reliable, and comprehensive coverage tailored to protect what matters most to you.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
          {services.map((service, i) => {
          const Icon = service.icon;
          return <div key={i} data-index={i} className={`relative group ${service.popular ? "lg:-translate-y-4" : ""}`}>
                {service.popular && <Badge className="absolute -top-3 right-6 z-10 bg-primary text-primary-foreground shadow-md uppercase tracking-wider text-[0.7rem] font-bold px-3 py-1">
                    Most Popular
                  </Badge>}

                <Card className="h-full min-h-[480px] bg-card border border-border p-10 flex flex-col transition-all duration-300 group-hover:-translate-y-1.5 group-hover:border-primary/60 group-hover:shadow-lg">
                  <CardContent className="p-0 flex flex-col h-full">
                    {}
                    <div className="w-20 h-20 bg-accent flex items-center justify-center mb-8 transition-colors duration-300 group-hover:bg-primary/15 shadow-sm">
                      <Icon className="w-9 h-9 text-primary" strokeWidth={1.75} />
                    </div>

                    {}
                    <h3 className="font-default text-2xl lg:text-[1.75rem] font-semibold text-foreground mb-4 leading-tight">
                      {service.title}
                    </h3>

                    {}
                    <p className="text-muted-foreground leading-relaxed mb-6">
                      {service.description}
                    </p>

                    {}
                    <ul className="space-y-3 mb-8">
                      {service.features.map((feature, idx) => <li key={idx} data-index={idx} className="flex items-start gap-3">
                          <span className="flex-shrink-0 w-5 h-5 bg-primary/15 flex items-center justify-center mt-0.5">
                            <Check className="w-3.5 h-3.5 text-primary" strokeWidth={3} />
                          </span>
                          <span className="text-sm text-foreground/85">
                            {feature}
                          </span>
                        </li>)}
                    </ul>

                    {}
                    <div className="mt-auto pt-2">
                      <Link to={service.to} className="inline-flex items-center gap-2 text-foreground font-semibold text-sm group/link">
                        <span>Learn more</span>
                        <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover/link:translate-x-1.5" />
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}